import { useMemo } from 'react';

import { useApiClient } from '@eluve/api-client-provider';

/** A reviewer's verdict on one pending finding. */
export interface ReviewDecision {
  resourceType: string;
  id: string;
  decision: 'approve' | 'reject';
}

/**
 * The mini-apps ts-rest surface, bound to a tenant + agent. Surfaces use this
 * for the generic install/grant/run/review lifecycle so they don't re-thread
 * `{ tenantId, agentId }` through every call.
 */
export function useMiniAppApi(tenantId: string, agentId: string) {
  const api = useApiClient();
  return useMemo(
    () => ({
      checkGrant: () => api.miniApps.grant({ params: { tenantId, agentId } }),
      install: () =>
        api.miniApps.install({ params: { tenantId, agentId }, body: {} }),
      run: (body: Record<string, unknown>) =>
        api.miniApps.run({ params: { tenantId, agentId }, body }),
      listFindings: (appointmentId: string) =>
        api.miniApps.findings({
          params: { tenantId, agentId },
          query: { appointmentId },
        }),
      review: (appointmentId: string, decisions: ReviewDecision[]) =>
        api.miniApps.review({
          params: { tenantId, agentId },
          body: { appointmentId, decisions },
        }),
      revert: (
        appointmentId: string,
        targets: Array<{ resourceType: string; id: string }>,
      ) =>
        api.miniApps.revert({
          params: { tenantId, agentId },
          body: { appointmentId, targets },
        }),
      editFinding: (body: {
        appointmentId: string;
        resourceType: string;
        id: string;
        display: string;
        system: string | null;
        code: string | null;
        /** Agent-specific edit payload, opaque to the SDK — the owning agent
         *  validates it (clinical-tasks: `ClinicalTaskEditExtras`). */
        extras?: Record<string, unknown>;
      }) =>
        api.miniApps.editFinding({
          params: { tenantId, agentId },
          body,
        }),
      dismissFinding: (
        appointmentId: string,
        target: { resourceType: string; id: string },
      ) =>
        api.miniApps.dismissFinding({
          params: { tenantId, agentId },
          body: { appointmentId, ...target },
        }),
      searchCodes: (searchTerm: string, system?: string) =>
        api.miniApps.searchCodes({
          params: { tenantId, agentId },
          query: { searchTerm, ...(system ? { system } : {}) },
        }),
      revoke: () =>
        api.miniApps.revoke({ params: { tenantId, agentId }, body: {} }),
    }),
    [api, tenantId, agentId],
  );
}
